/**
 * Which rows a run covers.
 *
 * Two answers to one question, which is what makes them chips: the whole
 * dataset, or the rows its filters match. Both are row-id enumerations over the
 * same DuckDB source, differing only in their predicate, which is what lets the
 * workflow selection apply identically to each. `all` deliberately ignores the
 * grid's filters rather than clearing them: the user keeps the view they were
 * in.
 *
 * "Rows that still need a run" is deliberately *not* one of these. It is a
 * second, independent narrowing — a predicate on run state rather than on which
 * rows — and while it sat in the same group as a third option, "the filtered
 * rows that still need a run" could not be said at all, even though the query
 * layer composes the two predicates without complaint.
 */
export type DatasetRunBasis = 'all' | 'filtered';

export const DATASET_RUN_BASES: readonly DatasetRunBasis[] = [
  'all',
  'filtered',
];

/**
 * The name on the chip. Sentence case rather than the shouted uppercase these
 * used to be set in: three uppercase labels in a row read as three section
 * headings, which is part of why it was unclear that they were a choice.
 */
export const DATASET_RUN_BASIS_LABEL: Record<DatasetRunBasis, string> = {
  all: 'All rows',
  filtered: 'Filtered rows',
};

/**
 * The one line under the chips, or null when the chips have already said it.
 *
 * There used to be two: a description of the picked basis and, beneath it, how
 * to narrow. On the common opening state that was two sentences of grey text
 * for a card whose whole job is a count and a button — and most of it restated
 * the label directly above it. "Filtered rows" does not need a line explaining
 * that it means the rows the filters match.
 *
 * What is left is only what a label cannot say: that "All rows" ignores the
 * narrowing the grid is already doing, and — when the grid is doing none — that
 * narrowing is possible at all.
 */
export const datasetRunBasisNote = ({
  basis,
  hasFilters,
}: {
  basis: DatasetRunBasis;
  hasFilters: boolean;
}): string | null => {
  if (basis !== 'all') {
    return null;
  }
  return hasFilters
    ? 'Ignores the filters on the grid.'
    : 'Filter a column to run on part of the dataset.';
};

/**
 * Whether a basis can be run right now, and what its chip should show on the
 * right: a count, or — when it is waiting on something the user does in the
 * grid — the two words that say what.
 *
 * A chip is never dropped for being unavailable. Hiding "Filtered rows" until
 * the grid was filtered made the card look like a run could not be narrowed at
 * all, which is the confusion this whole control exists to answer; showing it
 * as a greyed "0" made it look broken instead. "Filter first" is neither: it
 * says the basis exists and how to reach it, in the chip that offers it.
 */
export const datasetRunBasisState = ({
  basis,
  hasFilters,
  rows,
}: {
  basis: DatasetRunBasis;
  hasFilters: boolean;
  rows: number;
}): {
  count: number | null;
  requirement: string | null;
  available: boolean;
} => {
  if (basis === 'filtered' && !hasFilters) {
    return { count: null, requirement: 'filter first', available: false };
  }
  return { count: rows, requirement: null, available: rows > 0 };
};
